import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, ArrowRight, CheckCircle2, Lock, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'buyer',  label: 'Buyer',  helper: 'I want leads for my business.' },
  { value: 'seller', label: 'Seller', helper: 'I generate leads businesses can buy.' },
  { value: 'both',   label: 'Both',   helper: 'I want to buy and sell leads.' },
];

const TRUST_PILLARS = [
  { icon: CheckCircle2, label: 'Business verification' },
  { icon: Lock,         label: 'Controlled access' },
  { icon: Sparkles,     label: 'Recorded transactions' },
];

export default function WaitlistSection() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('both');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {position, role} or null
  const [totalCount, setTotalCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/waitlist/stats`)
      .then((r) => setTotalCount(r.data?.total ?? null))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/waitlist`, {
        email: email.trim(),
        role,
        company: company.trim() || undefined,
        source: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
      });
      setSubmitted({ position: res.data.position, status: res.data.status });
      toast.success(res.data.status === 'created' ? "You're on the list" : 'Updated your preferences');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Please enter a valid email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="waitlist"
      className="py-20 bg-gray-50 border-y border-gray-200"
      data-testid="waitlist-section"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium">
            Early Access
          </span>
          {typeof totalCount === 'number' && totalCount > 0 && (
            <span
              className="text-xs font-mono text-gray-500"
              data-testid="waitlist-count"
            >
              · {totalCount} {totalCount === 1 ? 'operator' : 'operators'} on the list
            </span>
          )}
        </div>

        <h2
          className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 max-w-3xl"
          data-testid="waitlist-headline"
        >
          Be part of a better way to buy and sell leads.
        </h2>

        <p
          className="mt-4 text-base sm:text-lg text-gray-600 max-w-3xl leading-relaxed"
          data-testid="waitlist-subheadline"
        >
          We're onboarding buyers and sellers across Australia. During early access, our team helps buyers define their requirements and connects them with relevant suppliers. Join as a buyer to tell us what you need, or as a seller to show us what you can supply.
        </p>

        {submitted ? (
          <div
            className="mt-8 bg-white border border-[#0055FF]/20 rounded-lg p-6 max-w-2xl"
            data-testid="waitlist-success"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0055FF]/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-[#0044CC]" />
              </div>
              <div className="flex-1">
                <div className="font-heading text-lg font-semibold text-gray-900">
                  You're in.
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {submitted.status === 'created' && submitted.position
                    ? `You're #${submitted.position} on the LeadXchange waitlist. `
                    : 'Your preferences are updated. '}
                  We'll email <span className="font-mono text-gray-900">{email}</span> the moment
                  your account is ready for verification.
                </p>
                <button
                  onClick={() => { setSubmitted(null); setEmail(''); setCompany(''); }}
                  className="mt-3 text-xs text-gray-500 hover:text-[#0044CC] transition-colors"
                  data-testid="waitlist-add-another"
                >
                  Add another person →
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-8 max-w-2xl"
            data-testid="waitlist-form"
          >
            <div className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6 shadow-sm">
              {/* Email + optional company */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-500 font-mono mb-1.5">
                    Work email <span className="text-[#0055FF]">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com.au"
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
                    data-testid="waitlist-email-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-500 font-mono mb-1.5">
                    Company <span className="text-gray-300 normal-case tracking-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Acme Broking Pty Ltd"
                    className="w-full h-11 px-3 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
                    data-testid="waitlist-company-input"
                  />
                </div>
              </div>

              {/* Role picker */}
              <div className="mt-5">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-mono mb-2">
                  I'm joining as
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {ROLE_OPTIONS.map((opt) => {
                    const selected = role === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`relative flex items-start gap-2.5 p-3 rounded-md border cursor-pointer transition-all ${
                          selected
                            ? 'border-[#0055FF] bg-[#0055FF]/5 ring-2 ring-[#0055FF]/20'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`waitlist-role-${opt.value}`}
                      >
                        <input
                          type="radio"
                          name="waitlist-role"
                          value={opt.value}
                          checked={selected}
                          onChange={() => setRole(opt.value)}
                          className="sr-only"
                        />
                        <span
                          className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 flex items-center justify-center ${
                            selected ? 'border-[#0055FF]' : 'border-gray-300'
                          }`}
                        >
                          {selected && (
                            <span className="w-2 h-2 rounded-full bg-[#0055FF]" />
                          )}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-gray-900">
                            {opt.label}
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {opt.helper}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || !email.trim()}
                className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors"
                data-testid="waitlist-submit"
              >
                {submitting ? 'Adding you…' : 'Join Early Access'}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>

              <div className="mt-3 text-[11px] text-gray-500">
                By joining, you agree to receive product updates from LeadXchange. Unsubscribe at any time.
              </div>
            </div>
          </form>
        )}

        {/* Trust pillars */}
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="waitlist-trust-pillars">
          {TRUST_PILLARS.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-sm text-gray-700">
              <Icon className="w-4 h-4 text-[#0044CC]" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
